import { useEffect, useState } from "react";

import { PlusSmallIcon } from "@heroicons/react/24/outline";

import DailyCheckDefinitionModal from "../../components/dailyChecks/DailyCheckDefinitionModal";
import DailyCheckDefinitionsTable from "../../components/dailyChecks/DailyCheckDefinitionsTable";
import DailyCheckStepModal from "../../components/dailyChecks/DailyCheckStepModal";
import DailyCheckStepsTable from "../../components/dailyChecks/DailyCheckStepsTable";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDailyChecks from "../../data/useDailyChecks";
import { IDailyCheckDefinition, IDailyCheckStep } from "../../models/DailyChecks";

export default function DailyChecksEditor() {
  const { setInfo } = usePageNameContext();
  const dailyCheckLabel = "Daily Check";
  const { definitions, updateDefinitionOrder, updateStepOrder } = useDailyChecks();
  const [dailyCheckDefinitions, setDailyCheckDefinitions] = useState<IDailyCheckDefinition[] | undefined>(undefined);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string | undefined>(undefined);
  const [editDefinition, setEditDefinition] = useState<IDailyCheckDefinition | undefined>(undefined);
  const [editStep, setEditStep] = useState<IDailyCheckStep | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [definitionOpen, setDefinitionOpen] = useState(false);
  const [stepOpen, setStepOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    setInfo({
      name: `${dailyCheckLabel}s Editor`,
      desc: `View and manage ${dailyCheckLabel.toLowerCase()}s and their steps`,
    });
  }, [setInfo, dailyCheckLabel]);

  useEffect(() => {
    if (!definitions.isLoading && definitions.data) {
      const orderedDefinitions = [...definitions.data.data].sort((a, b) => a.order - b.order);
      setDailyCheckDefinitions(orderedDefinitions);
      setIsLoading(false);
    }
  }, [definitions.isLoading, definitions.data]);

  const selectedDefinition = dailyCheckDefinitions?.find((x) => x.id === selectedDefinitionId);
  const selectedSteps = selectedDefinition
    ? [...selectedDefinition.steps].sort((a, b) => a.order - b.order)
    : undefined;

  const handleSelectDefinition = (definition: IDailyCheckDefinition) => {
    setSelectedDefinitionId(definition.id);
  };

  const handleEditDefinition = (definition: IDailyCheckDefinition) => {
    setEditDefinition(definition);
    setDefinitionOpen(true);
  };

  const handleAddDefinition = () => {
    setEditDefinition(undefined);
    setDefinitionOpen(true);
  };

  const handleSetStep = (step: IDailyCheckStep) => {
    setEditStep(step);
    setStepOpen(true);
  };

  const handleAddStep = () => {
    setEditStep(undefined);
    setStepOpen(true);
  };

  const handleMoveDefinition = async (definitionId: string, direction: "up" | "down") => {
    if (!dailyCheckDefinitions || isReordering) return;

    const orderedDefinitions = [...dailyCheckDefinitions].sort((a, b) => a.order - b.order);
    const index = orderedDefinitions.findIndex((x) => x.id === definitionId);

    if (index < 0) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= orderedDefinitions.length) return;

    setIsReordering(true);

    try {
      await new Promise<void>((resolve, reject) => {
        updateDefinitionOrder.mutate(
          { definitionId: definitionId, newIndex: targetIndex },
          {
            onSuccess: () => resolve(),
            onError: reject,
          }
        );
      });

      await definitions.refetch();
    } catch {
      await definitions.refetch();
    } finally {
      setIsReordering(false);
    }
  };

  const handleMoveStep = async (stepId: string, direction: "up" | "down") => {
    if (!selectedSteps || isReordering) return;

    const index = selectedSteps.findIndex((x) => x.id === stepId);

    if (index < 0) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= selectedSteps.length) return;

    setIsReordering(true);

    try {
      await new Promise<void>((resolve, reject) => {
        updateStepOrder.mutate(
          { stepId: stepId, newIndex: targetIndex },
          {
            onSuccess: () => resolve(),
            onError: reject,
          }
        );
      });

      await definitions.refetch();
    } catch {
      await definitions.refetch();
    } finally {
      setIsReordering(false);
    }
  };

  const nextDefinitionOrder = (dailyCheckDefinitions?.length ?? 0) + 1;
  const nextStepOrder = (selectedSteps?.length ?? 0) + 1;

  return definitions.isLoading || isLoading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <>
      <DailyCheckDefinitionModal
        open={definitionOpen}
        setOpen={setDefinitionOpen}
        definition={editDefinition}
        defaultOrder={nextDefinitionOrder}
      />
      {selectedDefinition && (
        <DailyCheckStepModal
          open={stepOpen}
          setOpen={setStepOpen}
          definitionId={selectedDefinition.id}
          step={editStep}
          defaultOrder={nextStepOrder}
        />
      )}
      <div className="mt-4 flex justify-between">
        <button
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
          type="button"
          onClick={handleAddDefinition}
        >
          <PlusSmallIcon className="h-5 w-5 mr-2" />
          Add New {dailyCheckLabel}
        </button>
      </div>

      <DailyCheckDefinitionsTable
        definitions={dailyCheckDefinitions!}
        selectedDefinitionId={selectedDefinitionId}
        selectDefinition={handleSelectDefinition}
        editDefinition={handleEditDefinition}
        moveDefinition={handleMoveDefinition}
        isReordering={isReordering}
      />

      {selectedDefinition ? (
        <>
          <div className="mt-8 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">{selectedDefinition.name} Steps</h2>
            <button
              className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
              type="button"
              onClick={handleAddStep}
            >
              <PlusSmallIcon className="h-5 w-5 mr-2" />
              Add New Step
            </button>
          </div>

          <DailyCheckStepsTable
            steps={selectedSteps!}
            setStep={handleSetStep}
            moveStep={handleMoveStep}
            isReordering={isReordering}
          />
        </>
      ) : (
        <p className="mt-8 text-sm text-gray-500">
          Select a {dailyCheckLabel.toLowerCase()} above to view and manage its steps.
        </p>
      )}
    </>
  );
}
